'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface WizardStep {
  id: number;
  title: string;
  description: string;
}

interface PreferenceOption {
  id: string;
  label: string;
  description: string;
  icon: string;
}

const BookingPreferenceWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPreferences, setSelectedPreferences] = useState<Record<number, string[]>>({});

  const steps: WizardStep[] = [
    {
      id: 1,
      title: "Vehicle Preferences",
      description: "Select your preferred vehicle types for different trip categories"
    },
    {
      id: 2,
      title: "Booking Policies",
      description: "Configure approval workflows and spending limits"
    },
    {
      id: 3,
      title: "Notification Settings",
      description: "Choose how and when you want to receive updates"
    },
    {
      id: 4,
      title: "Integration Setup",
      description: "Connect your existing business tools"
    }
  ];

  const vehicleOptions: PreferenceOption[] = [
    {
      id: "luxury-sedan",
      label: "Luxury Sedan",
      description: "Executive meetings & airport transfers",
      icon: "TruckIcon"
    },
    {
      id: "suv",
      label: "Premium SUV",
      description: "Group travel & client entertainment",
      icon: "TruckIcon"
    },
    {
      id: "executive-van",
      label: "Executive Van",
      description: "Team outings & conference shuttles",
      icon: "TruckIcon"
    },
    {
      id: "stretch-limo",
      label: "Stretch Limousine",
      description: "Special events & VIP occasions",
      icon: "TruckIcon"
    }
  ];

  const policyOptions: PreferenceOption[] = [
    {
      id: "auto-approve",
      label: "Auto-Approve Under $500",
      description: "Trips below threshold don't require approval",
      icon: "CheckCircleIcon"
    },
    {
      id: "manager-approval",
      label: "Manager Approval Required",
      description: "All bookings need department head sign-off",
      icon: "UserIcon"
    },
    {
      id: "budget-alerts",
      label: "Budget Threshold Alerts",
      description: "Notify when approaching monthly limits",
      icon: "BellAlertIcon"
    },
    {
      id: "preferred-routes",
      label: "Preferred Route Enforcement",
      description: "Suggest cost-effective routing options",
      icon: "MapIcon"
    }
  ];

  const notificationOptions: PreferenceOption[] = [
    {
      id: "email-updates",
      label: "Email Notifications",
      description: "Booking confirmations & trip updates",
      icon: "EnvelopeIcon"
    },
    {
      id: "sms-alerts",
      label: "SMS Alerts",
      description: "Real-time driver arrival notifications",
      icon: "DevicePhoneMobileIcon"
    },
    {
      id: "dashboard-only",
      label: "Dashboard Only",
      description: "Check updates in your account portal",
      icon: "ComputerDesktopIcon"
    },
    {
      id: "slack-integration",
      label: "Slack Integration",
      description: "Trip updates in your team channel",
      icon: "ChatBubbleLeftRightIcon"
    }
  ];

  const integrationOptions: PreferenceOption[] = [
    {
      id: "concur",
      label: "SAP Concur",
      description: "Automated expense reporting",
      icon: "DocumentTextIcon"
    },
    {
      id: "expensify",
      label: "Expensify",
      description: "Receipt capture & reimbursement",
      icon: "ReceiptPercentIcon"
    },
    {
      id: "calendar",
      label: "Calendar Sync",
      description: "Google/Outlook calendar integration",
      icon: "CalendarIcon"
    },
    {
      id: "api",
      label: "Custom API",
      description: "Connect your internal systems",
      icon: "CodeBracketIcon"
    }
  ];

  const getOptionsForStep = (step: number): PreferenceOption[] => {
    switch (step) {
      case 1:
        return vehicleOptions;
      case 2:
        return policyOptions;
      case 3:
        return notificationOptions;
      case 4:
        return integrationOptions;
      default:
        return [];
    }
  };

  const togglePreference = (optionId: string) => {
    setSelectedPreferences(prev => {
      const currentStepPrefs = prev[currentStep] || [];
      const isSelected = currentStepPrefs.includes(optionId);
      
      return {
        ...prev,
        [currentStep]: isSelected
          ? currentStepPrefs.filter(id => id !== optionId)
          : [...currentStepPrefs, optionId]
      };
    });
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    alert('Preferences saved successfully! Your account manager will review and implement these settings within 24 hours.');
  };

  return (
    <section data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:200:4" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="200" data-component-end-line="340" data-component-file="BookingPreferenceWizard.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-20%20bg-muted%22%7D" className="py-20 bg-muted">
      <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:201:6" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="201" data-component-end-line="339" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-5xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:202:8" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="202" data-component-end-line="209" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
          <h2 data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:203:10" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="203" data-component-end-line="205" data-component-file="BookingPreferenceWizard.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-4xl%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Customize%20Your%20Corporate%20Account%22%7D" className="font-playfair font-bold text-4xl text-foreground mb-4">
            Customize Your Corporate Account
          </h2>
          <p data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:206:10" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="206" data-component-end-line="208" data-component-file="BookingPreferenceWizard.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%22%2C%22textContent%22%3A%22Set%20up%20your%20booking%20preferences%20in%20just%20a%20few%20minutes%22%7D" className="text-lg text-muted-foreground">
            Set up your booking preferences in just a few minutes
          </p>
        </div>

        <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:211:8" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="211" data-component-end-line="338" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-2xl%20shadow-executive%20p-8%22%7D" className="bg-card rounded-2xl shadow-executive p-8">
          {/* Progress Steps */}
          <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:213:10" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="213" data-component-end-line="250" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%22%7D" className="mb-12">
            <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:214:12" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="214" data-component-end-line="249" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:216:16" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="216" data-component-end-line="247" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20flex-1%22%7D" key={step.id} className="flex items-center flex-1">
                  <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:217:18" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="217" data-component-end-line="239" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20flex-1%22%7D" className="flex flex-col items-center flex-1">
                    <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:218:20" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="218" data-component-end-line="231" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-montserrat font-bold transition-smooth ${
                        currentStep === step.id
                          ? 'bg-primary text-primary-foreground'
                          : currentStep > step.id
                          ? 'bg-success text-white' :'bg-muted text-muted-foreground'
                      }`}
                    >
                      {currentStep > step.id ? (
                        <Icon data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:227:24" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="227" data-component-end-line="227" data-component-file="BookingPreferenceWizard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%7D" name="CheckIcon" size={24} />
                      ) : (
                        step.id
                      )}
                    </div>
                    <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:232:20" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="232" data-component-end-line="238" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mt-3%20hidden%20sm%3Ablock%22%7D" className="text-center mt-3 hidden sm:block">
                      <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:233:22" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="233" data-component-end-line="237" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`text-sm font-montserrat font-semibold ${
                        currentStep === step.id ? 'text-primary' : 'text-muted-foreground'
                      }`}>
                        {step.title}
                      </div>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:241:20" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="241" data-component-end-line="245" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                      className={`h-1 flex-1 mx-2 transition-smooth ${
                        currentStep > step.id ? 'bg-success' : 'bg-muted'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Current Step Content */}
          <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:253:10" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="253" data-component-end-line="299" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
            <h3 data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:254:12" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="254" data-component-end-line="256" data-component-file="BookingPreferenceWizard.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-2xl%20text-foreground%20mb-2%22%7D" className="font-playfair font-bold text-2xl text-foreground mb-2">
              {steps[currentStep - 1].title}
            </h3>
            <p data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:257:12" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="257" data-component-end-line="259" data-component-file="BookingPreferenceWizard.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20mb-8%22%7D" className="text-muted-foreground mb-8">
              {steps[currentStep - 1].description}
            </p>

            <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:261:12" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="261" data-component-end-line="298" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20sm%3Agrid-cols-2%20gap-4%22%7D" className="grid sm:grid-cols-2 gap-4">
              {getOptionsForStep(currentStep).map((option) => {
                const isSelected = selectedPreferences[currentStep]?.includes(option.id);
                return (
                  <button data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:265:18" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="265" data-component-end-line="295" data-component-file="BookingPreferenceWizard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                    key={option.id}
                    onClick={() => togglePreference(option.id)}
                    className={`p-6 rounded-xl border-2 text-left transition-smooth ${
                      isSelected
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:273:20" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="273" data-component-end-line="294" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20space-x-4%22%7D" className="flex items-start space-x-4">
                      <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:274:22" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="274" data-component-end-line="282" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-primary' : 'bg-muted'
                      }`}>
                        <Icon data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:277:24" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="277" data-component-end-line="281" data-component-file="BookingPreferenceWizard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D"
                          name={option.icon as any}
                          size={20}
                          className={isSelected ? 'text-primary-foreground' : 'text-muted-foreground'}
                        />
                      </div>
                      <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:283:22" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="283" data-component-end-line="290" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
                        <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:284:24" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="284" data-component-end-line="286" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-montserrat%20font-semibold%20text-foreground%20mb-1%22%7D" className="font-montserrat font-semibold text-foreground mb-1">
                          {option.label}
                        </div>
                        <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:287:24" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="287" data-component-end-line="289" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%7D" className="text-sm text-muted-foreground">
                          {option.description}
                        </div>
                      </div>
                      {isSelected && (
                        <Icon data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:292:24" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="292" data-component-end-line="292" data-component-file="BookingPreferenceWizard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22text-primary%20flex-shrink-0%22%7D" name="CheckCircleIcon" size={24} className="text-primary flex-shrink-0" variant="solid" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:302:10" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="302" data-component-end-line="337" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20pt-8%20border-t%20border-border%22%7D" className="flex items-center justify-between pt-8 border-t border-border">
            <button data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:303:12" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="303" data-component-end-line="314" data-component-file="BookingPreferenceWizard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Previous%22%7D"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`inline-flex items-center px-6 py-3 rounded-md font-montserrat font-semibold transition-smooth ${
                currentStep === 1
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              <Icon data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:312:14" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="312" data-component-end-line="312" data-component-file="BookingPreferenceWizard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowLeftIcon%22%2C%22className%22%3A%22mr-2%22%7D" name="ArrowLeftIcon" size={20} className="mr-2" />
              Previous
            </button>

            <div data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:316:12" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="316" data-component-end-line="318" data-component-file="BookingPreferenceWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Step%20of%22%7D" className="text-sm text-muted-foreground">
              Step {currentStep} of {steps.length}
            </div>

            {currentStep < steps.length ? (
              <button data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:321:14" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="321" data-component-end-line="327" data-component-file="BookingPreferenceWizard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22inline-flex%20items-center%20px-6%20py-3%20bg-primary%20text-primary-foreground%20rounded-md%20font-montserrat%20font-semibold%20transition-smooth%20hover%3Abg-opacity-90%22%2C%22textContent%22%3A%22Next%20Step%22%7D"
                onClick={handleNext}
                className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-md font-montserrat font-semibold transition-smooth hover:bg-opacity-90"
              >
                Next Step
                <Icon data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:326:16" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="326" data-component-end-line="326" data-component-file="BookingPreferenceWizard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowRightIcon%22%2C%22className%22%3A%22ml-2%22%7D" name="ArrowRightIcon" size={20} className="ml-2" />
              </button>
            ) : (
              <button data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:329:14" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="329" data-component-end-line="335" data-component-file="BookingPreferenceWizard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22inline-flex%20items-center%20px-6%20py-3%20bg-success%20text-white%20rounded-md%20font-montserrat%20font-semibold%20transition-smooth%20hover%3Abg-opacity-90%22%2C%22textContent%22%3A%22Complete%20Setup%22%7D"
                onClick={handleComplete}
                className="inline-flex items-center px-6 py-3 bg-success text-white rounded-md font-montserrat font-semibold transition-smooth hover:bg-opacity-90"
              >
                Complete Setup
                <Icon data-component-id="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx:334:16" data-component-path="src/app/corporate-solutions/components/BookingPreferenceWizard.tsx" data-component-line="334" data-component-end-line="334" data-component-file="BookingPreferenceWizard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%2C%22className%22%3A%22ml-2%22%7D" name="CheckIcon" size={20} className="ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingPreferenceWizard;